import {
  BioBlend,
  Bunker,
  BunkerResponse,
  ReportFormInterface,
  ShipReportingRequirements,
  SubmittedReport,
} from 'src/types';
import { compareDate } from './sort.utils';
import { v4 as uuidv4 } from 'uuid';
import { getLocalTime, mapTimezone } from './date.utils';
import { toFiniteDecimal } from './number.utils';
import {
  findMatchingBlendComponent,
  getPrimaryDocumentUuid,
  mergeBlendDocumentMetadata,
  mergeDocumentGeneration,
} from './document.utils';

export const extractDuplicateReportId = (input: string): number | null => {
  const match = input.match(/\b(?:duplicate with\s+|duplicate report id:\s*)([1-9]\d*)(?![\w-])/i);
  const id = match ? Number(match[1]) : NaN;
  return Number.isSafeInteger(id) ? id : null;
};

export const processReports = (localReports: ReportFormInterface[]): ReportFormInterface[] => {
  let reports: ReportFormInterface[] = JSON.parse(JSON.stringify(localReports));
  const bunkerGrades = new Set();

  localReports.forEach((report: ReportFormInterface) => {
    report.bunkers.forEach((bunker) => {
      bunkerGrades.add(bunker.grade);
    });
  });

  for (const grade of bunkerGrades) {
    for (const report of reports) {
      let bunkerFound = false;
      report.bunkers = report.bunkers.map((bunker: Bunker) => {
        if (bunker.grade === grade) {
          bunkerFound = true;
          return { ...bunker, isInitial: true };
        }
        return bunker;
      });
      if (bunkerFound) {
        break;
      }
    }
  }

  reports = reports.map((report: ReportFormInterface, idx: number) => {
    const firstReportEntry = idx === 0;
    const { bunkers: previousBunkers } = firstReportEntry ? { bunkers: [] } : localReports[idx - 1];

    const bunkers = report.bunkers.map((bunker, bunkerIndex) => {
      // Read original values so the JSON copy cannot turn an invalid number into a missing receipt.
      const currentBunker = localReports[idx].bunkers[bunkerIndex];
      const { grade } = currentBunker;
      const previousBunker = previousBunkers.find(({ grade: bunkerType }) => bunkerType === grade);
      return {
        ...bunker,
        calculatedConsumptionMt: calculateBunkerConsumption(currentBunker, previousBunker),
      };
    });

    return { ...report, bunkers };
  });

  return reports;
};

const calculateBunkerConsumption = (bunker: Bunker, previousBunker?: Bunker): number | undefined => {
  if (bunker.grade === 'lng' && bunker.remainingOnboardMt === '') {
    const consumption = toFiniteDecimal(bunker.consumptionMt)?.toNumber();
    return Number.isFinite(consumption) ? consumption : undefined;
  }

  const previousRemaining = toFiniteDecimal(previousBunker?.remainingOnboardMt);
  const remaining = toFiniteDecimal(bunker.remainingOnboardMt);
  const receivedValue = String(bunker.receivedMt ?? '').trim();
  // Older synced reports can contain these strings from String(null) / String(undefined).
  const hasNoReceipt = receivedValue === '' || receivedValue === 'null' || receivedValue === 'undefined';
  const received = toFiniteDecimal(hasNoReceipt ? 0 : bunker.receivedMt);
  if (!previousRemaining || !remaining || !received) {
    return undefined;
  }

  const consumption = previousRemaining.plus(received).minus(remaining).toNumber();
  return Number.isFinite(consumption) ? consumption : undefined;
};

export const mergeReports = (
  submittedReports: SubmittedReport[],
  reports: ReportFormInterface[]
): ReportFormInterface[] => {
  const localIdsMap: Record<string, string> = {};
  reports
    .filter((report) => report.localId)
    .forEach((report) => {
      localIdsMap[String(report.id)] = report.localId;
    });
  let result = [] as ReportFormInterface[];
  const notSyncedReportIds: string[] = [];
  const formattedReports = submittedReports.map((item: SubmittedReport) => {
    const edn = item.edns?.[0];
    const localReport = reports.find((localItem) => localItem.id === item.id);
    const localCreatedReport = localReport ?? reports.find((localItem) => String(localItem.id) === String(item.id));
    let report: ReportFormInterface = {
      id: item.id,
      serverVersion: item.version,
      issueAccessToken: item.issueAccessToken || localCreatedReport?.issueAccessToken,
      locked: item.locked,
      created: item.created ?? localCreatedReport?.created,
      localId: localIdsMap[String(item.id)] ? localIdsMap[String(item.id)] : uuidv4(),
      condition: item.condition,
      location: item.location,
      reportType: item.reportType,
      reportedTime: item.reportedTimeLocal,
      reportDate: item.reportedTimeLocal.slice(0, 10),
      dateString: item.reportedTimeLocal.slice(0, 10),
      localTime: item.reportedTimeLocal.slice(11, 16),
      sailedDistanceNm: item.sailedDistanceNm !== null ? String(item.sailedDistanceNm) : null,
      timeSinceLastReport: item.timeSinceLastReport !== null ? String(item.timeSinceLastReport) : null,
      timezone: mapTimezone(item.utcOffset).value,
      cargoQuantityCars: item.cargoQuantityCars !== null ? String(item.cargoQuantityCars) : null,
      cargoQuantityM3: item.cargoQuantityM3 !== null ? String(item.cargoQuantityM3) : null,
      cargoQuantityMt: item.cargoQuantityMT !== null ? String(item.cargoQuantityMT) : null,
      cargoQuantityTEU: item.cargoQuantityTEU !== null ? String(item.cargoQuantityTEU) : null,
      ctmsN2MolPct: item.ctmsN2MolPct != null ? String(item.ctmsN2MolPct) : null,
      ctmsLngAverageMolarMassKgPerKmol:
        item.ctmsLngAverageMolarMassKgPerKmol != null ? String(item.ctmsLngAverageMolarMassKgPerKmol) : null,
      ctmsDocumentUuid: item.ctmsDocumentUuid ?? null,
      latitude: item.latitude !== null ? String(item.latitude) : null,
      longitude: item.longitude !== null ? String(item.longitude) : null,
      modeOfOperation: item.modeOfOperation,
      nextCargoPort: item.nextCargoPort,
      opsDurationHRS: item.opsDurationHRS !== null ? String(item.opsDurationHRS) : null,
      opsKWH: item.opsKWH !== null ? String(item.opsKWH) : null,
      port: item.port,
      portActivity: item.portActivity,
      previousCargoPort: item.previousCargoPort,
      remark: item.remark,
      speedOverGroundKt: item.speedOverGroundKT !== null ? String(item.speedOverGroundKT) : null,
      speedThroughWaterKt: item.speedThroughWaterKT !== null ? String(item.speedThroughWaterKT) : null,
      timeAtAnchor: item.timeAtAnchor !== null ? String(item.timeAtAnchor) : null,
      timeDrifting: item.timeDrifting !== null ? String(item.timeDrifting) : null,
      bunkers: formatBunkers(item.bunkers),
      synced: true,
      ednUuid: edn ? edn.uuid : null,
      ednDocumentUuid: edn ? getPrimaryDocumentUuid(edn.documentUuid, edn.documents) : null,
      ednNumber: edn ? edn.ednNumber : null,
      ednDate: edn ? getLocalTime(edn.date, edn.utcOffset).slice(0, 10) : null,
      ednTime: edn ? getLocalTime(edn.date, edn.utcOffset).slice(11, 16) : null,
      ednUtcOffset: edn ? mapTimezone(edn.utcOffset).value : null,
    };
    if (localReport) {
      report = preserveLocalBdnDocumentMetadata(report, localReport);
      report = preserveLocalEdnDocumentMetadata(report, localReport);
      report = preserveLocalCtmsDocumentMetadata(report, localReport);
    }
    return report;
  });
  for (const report of reports) {
    const isExist = formattedReports.find((item) => item.id === report.id);
    const isReportedTimeExist = formattedReports.some(
      (item) => item.id !== report.id && item.reportedTime === report.reportedTime
    );
    if (report.synced === true && !isExist) {
      result.push(report);
    } else if (report.synced === false && isExist && !isReportedTimeExist) {
      notSyncedReportIds.push(String(report.id));
      result.push(mergeSubmittedDocumentMetadataIntoLocalReport(report, isExist));
    } else if (report.synced === false && !isExist && !isReportedTimeExist) {
      result.push(report);
    }
  }
  result = [
    ...result,
    ...formattedReports.filter((item: ReportFormInterface) => !notSyncedReportIds.includes(String(item.id))),
  ];
  result.sort((x: ReportFormInterface, y: ReportFormInterface) =>
    compareDate(new Date(x.reportedTime), new Date(y.reportedTime), true)
  );
  return result;
};

export const getMissingEndOfMonthReportDate = (
  reports: ReportFormInterface[],
  now: Date,
  requirements: ShipReportingRequirements
): Date | null => {
  if (!requirements.continuousReportingRequired) return null;

  const targetTime = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0, 23, 59);
  const targetMinuteEnd = targetTime + 60_000;
  const submittedForTime = requirements.endOfMonthNoonSubmittedFor
    ? new Date(requirements.endOfMonthNoonSubmittedFor).getTime()
    : Number.NaN;
  const hasRequiredReport = (
    !Number.isNaN(submittedForTime)
    && submittedForTime >= targetTime
    && submittedForTime < targetMinuteEnd
  ) || reports.some((report) => {
    const reportedTime = new Date(report.reportedTime).getTime();
    return !Number.isNaN(reportedTime) && reportedTime >= targetTime && reportedTime < targetMinuteEnd;
  });

  return hasRequiredReport ? null : new Date(targetTime);
};

export const hydrateBlendMetadataIntoReports = (
  reports: ReportFormInterface[],
  bioBlends: BioBlend[]
): ReportFormInterface[] => {
  if (bioBlends.length === 0) {
    return reports;
  }

  return reports.map((report) => ({
    ...report,
    bunkers: report.bunkers.map((bunker) => hydrateBlendMetadataIntoBunker(bunker, bioBlends)),
  }));
};

const preserveLocalBdnDocumentMetadata = (
  report: ReportFormInterface,
  localReport: ReportFormInterface
): ReportFormInterface => {
  const bunkers = report.bunkers.map((bunker) => {
    const localBunker = localReport.bunkers.find((localItem) => {
      if (localItem.id && bunker.id) {
        return localItem.id === bunker.id;
      }

      if (localItem.grade !== bunker.grade) {
        return false;
      }

      const localBdnUuid = localItem.bunkerDeliveryNote?.uuid;
      const bunkerBdnUuid = bunker.bunkerDeliveryNote?.uuid;
      if (localBdnUuid && bunkerBdnUuid) {
        return localBdnUuid === bunkerBdnUuid;
      }

      return true;
    });

    const localDocumentUuid = getPrimaryDocumentUuid(
      localBunker?.bunkerDeliveryNote?.documentUuid,
      localBunker?.bunkerDeliveryNote?.documents
    );
    const bdnRecordUuid = bunker.bunkerDeliveryNote?.uuid;
    const bdnDocument = mergeDocumentGeneration(
      localBunker?.bunkerDeliveryNote?.bdnDocumentId,
      localDocumentUuid,
      bunker.bunkerDeliveryNote?.bdnDocumentId,
      getPrimaryDocumentUuid(
        bunker.bunkerDeliveryNote?.documentUuid,
        bunker.bunkerDeliveryNote?.documents
      )
    );
    // Don't preserve documentUuid if it equals the BDN record UUID — that's a legacy data bug
    const documentUuid = bdnDocument.documentUuid !== bdnRecordUuid ? bdnDocument.documentUuid : null;
    const bdnDocumentName = localBunker?.bunkerDeliveryNote?.bdnDocumentName;
    const bdnDocumentId = bdnDocument.documentId;
    const bdnFieldSources = localBunker?.bunkerDeliveryNote?.bdnFieldSources;
    if (!documentUuid && !bdnDocumentId && !bdnDocumentName && !bdnFieldSources) {
      return bunker;
    }

    return {
      ...bunker,
      bunkerDeliveryNote: {
        ...bunker.bunkerDeliveryNote,
        documentUuid,
        bdnDocumentId,
        bdnDocumentName,
        bdnFieldSources: bdnFieldSources ?? {},
      },
    };
  });

  return {
    ...report,
    bunkers,
  };
};

const preserveLocalEdnDocumentMetadata = (
  report: ReportFormInterface,
  localReport: ReportFormInterface
): ReportFormInterface => {
  if (!localReport.ednDocumentName && !localReport.ednDocumentUuid && !localReport.ednDocumentId) {
    return report;
  }

  const ednDocument = mergeDocumentGeneration(
    localReport.ednDocumentId,
    localReport.ednDocumentUuid,
    report.ednDocumentId,
    report.ednDocumentUuid
  );
  return {
    ...report,
    ednDocumentUuid: ednDocument.documentUuid,
    ednDocumentId: ednDocument.documentId,
    ednDocumentName: localReport.ednDocumentName,
  };
};

const preserveLocalCtmsDocumentMetadata = (
  report: ReportFormInterface,
  localReport: ReportFormInterface
): ReportFormInterface => {
  if (!localReport.ctmsDocumentName && !localReport.ctmsDocumentUuid && !localReport.ctmsDocumentId) {
    return report;
  }
  const document = mergeDocumentGeneration(
    localReport.ctmsDocumentId,
    localReport.ctmsDocumentUuid,
    report.ctmsDocumentId,
    report.ctmsDocumentUuid
  );
  return {
    ...report,
    ctmsDocumentUuid: document.documentUuid,
    ctmsDocumentId: document.documentId,
    ctmsDocumentName: localReport.ctmsDocumentName,
  };
};

const mergeSubmittedDocumentMetadataIntoLocalReport = (
  localReport: ReportFormInterface,
  submittedReport: ReportFormInterface
): ReportFormInterface => {
  const bunkers = localReport.bunkers.map((localBunker) => {
    const submittedBunker = submittedReport.bunkers.find((submittedItem) => {
      if (localBunker.id && submittedItem.id) {
        return localBunker.id === submittedItem.id;
      }

      if (localBunker.grade !== submittedItem.grade) {
        return false;
      }

      const localBdnUuid = localBunker.bunkerDeliveryNote?.uuid;
      const submittedBdnUuid = submittedItem.bunkerDeliveryNote?.uuid;
      if (localBdnUuid && submittedBdnUuid) {
        return localBdnUuid === submittedBdnUuid;
      }

      const localBlendUuid = localBunker.bioBlend?.uuid;
      const submittedBlendUuid = submittedItem.bioBlend?.uuid;
      if (localBlendUuid && submittedBlendUuid) {
        return localBlendUuid === submittedBlendUuid;
      }

      return true;
    });

    if (!submittedBunker) {
      return localBunker;
    }

    const submittedBioBlend =
      submittedBunker.bioBlend && Object.keys(submittedBunker.bioBlend).length !== 0 ? submittedBunker.bioBlend : null;
    const localBioBlend =
      localBunker.bioBlend && Object.keys(localBunker.bioBlend).length !== 0 ? localBunker.bioBlend : null;

    const components = localBioBlend?.components
      ? localBioBlend.components.map((localComponent, index) => {
          const submittedComponent = findMatchingBlendComponent(
            localComponent,
            submittedBioBlend?.components || [],
            index
          );

          if (!submittedComponent) {
            return localComponent;
          }

          const posDocument = mergeDocumentGeneration(
            localComponent.posDocumentId,
            localComponent.posDocumentUuid,
            submittedComponent.posDocumentId,
            submittedComponent.posDocumentUuid
          );
          return {
            ...localComponent,
            posDocumentId: posDocument.documentId,
            posDocumentUuid: posDocument.documentUuid,
          };
        })
      : submittedBioBlend?.components;

    return {
      ...localBunker,
      bunkerDeliveryNote:
        localBunker.bunkerDeliveryNote || submittedBunker.bunkerDeliveryNote
          ? (() => {
              const bdnRecordUuid =
                submittedBunker.bunkerDeliveryNote?.uuid ?? localBunker.bunkerDeliveryNote?.uuid;
              const bdnDocument = mergeDocumentGeneration(
                localBunker.bunkerDeliveryNote?.bdnDocumentId,
                getPrimaryDocumentUuid(
                  localBunker.bunkerDeliveryNote?.documentUuid,
                  localBunker.bunkerDeliveryNote?.documents
                ),
                submittedBunker.bunkerDeliveryNote?.bdnDocumentId,
                getPrimaryDocumentUuid(
                  submittedBunker.bunkerDeliveryNote?.documentUuid,
                  submittedBunker.bunkerDeliveryNote?.documents
                )
              );
              return {
                ...submittedBunker.bunkerDeliveryNote,
                ...localBunker.bunkerDeliveryNote,
                // Don't carry forward documentUuid if it equals the BDN record UUID — legacy bug
                documentUuid: bdnDocument.documentUuid !== bdnRecordUuid ? bdnDocument.documentUuid : null,
                bdnDocumentId: bdnDocument.documentId,
              };
            })()
          : localBunker.bunkerDeliveryNote,
      bioBlend: localBioBlend
        ? (() => {
            const bdnDocument = mergeDocumentGeneration(
              localBioBlend.bdnDocumentId,
              localBioBlend.bdnDocumentUuid,
              submittedBioBlend?.bdnDocumentId,
              submittedBioBlend?.bdnDocumentUuid
            );
            return {
              ...localBioBlend,
              bdnUuid: localBioBlend.bdnUuid || submittedBioBlend?.bdnUuid || '',
              bdnDocumentId: bdnDocument.documentId,
              bdnDocumentUuid: bdnDocument.documentUuid,
              components: components ?? localBioBlend.components ?? [],
            };
          })()
        : submittedBioBlend || localBunker.bioBlend,
    };
  });

  const ednDocument = mergeDocumentGeneration(
    localReport.ednDocumentId,
    localReport.ednDocumentUuid,
    submittedReport.ednDocumentId,
    submittedReport.ednDocumentUuid
  );
  const ctmsDocument = mergeDocumentGeneration(
    localReport.ctmsDocumentId,
    localReport.ctmsDocumentUuid,
    submittedReport.ctmsDocumentId,
    submittedReport.ctmsDocumentUuid
  );
  return {
    ...localReport,
    ednUuid: localReport.ednUuid ?? submittedReport.ednUuid ?? null,
    ednDocumentId: ednDocument.documentId,
    ednDocumentUuid: ednDocument.documentUuid,
    ctmsDocumentId: ctmsDocument.documentId,
    ctmsDocumentUuid: ctmsDocument.documentUuid,
    bunkers,
  };
};

const formatBunkers = (bunkers: BunkerResponse[]): Bunker[] => {
  const result: Bunker[] = [];
  for (const bunker of bunkers) {
    const blend = bunker.blend;
    const bdn = bunker.bdn.uuid ? bunker.bdn : (blend?.bdn ?? bunker.bdn);
    const formattedBunker: Bunker = {
      id: bunker.id,
      grade: blend?.name ? blend.name.toLowerCase() : bunker.grade,
      remainingOnboardMt: String(bunker.remainingOnBoardMT ?? ''),
      consumptionMt: String(bunker.consumptionMT ?? ''),
      receivedMt: String(bunker.receivedMT ?? ''),
      liquefiedNaturalGasN2ConsumptionMT: String(bunker.liquefiedNaturalGasN2ConsumptionMT ?? ''),
      ciiCf: {
        coolingKWH: String(bunker.ciiCorrectionFactor.coolingKWH ?? 0),
        coolingSFOCGKWH: String(bunker.ciiCorrectionFactor.coolingSFOCGKWH ?? 0),
        dischargeBoilerMT: String(bunker.ciiCorrectionFactor.dischargeBoilerMT ?? 0),
        dischargeKWH: String(bunker.ciiCorrectionFactor.dischargeKWH ?? 0),
        dischargeSFOCGKWH: String(bunker.ciiCorrectionFactor.dischargeSFOCGKWH ?? 0),
        dischargeStandaloneMT: String(bunker.ciiCorrectionFactor.dischargeStandaloneMT ?? 0),
        heatingBoilerMT: String(bunker.ciiCorrectionFactor.heatingBoilerMT ?? 0),
        id: bunker.ciiCorrectionFactor.id,
        reportBunkerID: bunker.id,
      },
      consumptionDetails: {
        mainEngineConsumptionMT: String(bunker.mainEngineConsumptionMT ?? ''),
        auxConsumptionMT: String(bunker.auxConsumptionMT ?? ''),
        boilerConsumptionMT: String(bunker.boilerConsumptionMT ?? ''),
        inertGasGeneratorConsumptionMT: String(bunker.inertGasGeneratorConsumptionMT ?? ''),
        gasCombustionUnitConsumptionMT: String(bunker.gasCombustionUnitConsumptionMT ?? ''),
      },
      bunkerDeliveryNote: {
        uuid: bdn.uuid,
        documentUuid: getPrimaryDocumentUuid(bdn.documentUuid, bdn.documents),
        bdnNumber: bdn.bdnNumber,
        bdnFuelType: bdn.bdnFuelType,
        bdnDate:
          bdn.bdnDate && bdn.bdnUtcOffset
            ? getLocalTime(bdn.bdnDate, bdn.bdnUtcOffset).slice(0, 10)
            : null,
        bdnTime:
          bdn.bdnDate && bdn.bdnUtcOffset
            ? getLocalTime(bdn.bdnDate, bdn.bdnUtcOffset).slice(11, 16)
            : null,
        bdnUtcOffset: bdn.bdnUtcOffset !== null ? mapTimezone(bdn.bdnUtcOffset).value : null,
        sulphurContent: bdn.sulphurContent !== null ? String(bdn.sulphurContent) : null,
        viscosity: bdn.viscosity !== null ? String(bdn.viscosity) : null,
        isoGrade: bdn.isoGrade,
      },
      bioBlend: blend?.name
        ? {
            uuid: blend.uuid ?? '',
            blendGrade: blend.grade,
            name: blend.name,
            components: (blend.components || []).map((component) => ({
              ...component,
              receivedMt: String(component.receivedMT),
              lcvPos: component.lcvPos === undefined ? null : component.lcvPos,
              euGhgIntensity: component.euGhgIntensity === undefined ? null : component.euGhgIntensity,
              sustainabilityReference:
                component.sustainabilityReference === undefined ? null : component.sustainabilityReference,
              posDocumentUuid: getPrimaryDocumentUuid(component.documentUuid, component.documents),
            })),
            bdnUuid: bdn.uuid ?? '',
            bdnDocumentUuid: getPrimaryDocumentUuid(bdn.documentUuid, bdn.documents),
            bdnNumber: bdn.bdnNumber ?? '',
            bdnDate:
              bdn.bdnDate && bdn.bdnUtcOffset
                ? getLocalTime(bdn.bdnDate, bdn.bdnUtcOffset).slice(0, 10)
                : '',
            bdnTime:
              bdn.bdnDate && bdn.bdnUtcOffset
                ? getLocalTime(bdn.bdnDate, bdn.bdnUtcOffset).slice(11, 16)
                : '',
            bdnUtcOffset: bdn.bdnUtcOffset !== null ? mapTimezone(bdn.bdnUtcOffset).value : '',
            sulphurContent: bdn.sulphurContent !== null ? String(bdn.sulphurContent) : null,
            viscosity: bdn.viscosity !== null ? String(bdn.viscosity) : null,
            isoGrade: bdn.isoGrade,
          }
        : ({} as BioBlend),
    };
    result.push(formattedBunker);
  }

  return result;
};

const hydrateBlendMetadataIntoBunker = (bunker: Bunker, bioBlends: BioBlend[]): Bunker => {
  const reportBlend = bunker.bioBlend && Object.keys(bunker.bioBlend).length !== 0 ? bunker.bioBlend : null;
  const localBlend = findMatchingLocalBlend(bunker, bioBlends);

  if (!reportBlend && !localBlend) {
    return bunker;
  }

  if (!reportBlend && localBlend) {
    return {
      ...bunker,
      bioBlend: localBlend,
    };
  }

  if (!reportBlend) {
    return bunker;
  }

  return {
    ...bunker,
    bioBlend: localBlend ? mergeBlendDocumentMetadata(reportBlend, localBlend) : reportBlend,
  };
};

const findMatchingLocalBlend = (bunker: Bunker, bioBlends: BioBlend[]): BioBlend | undefined => {
  const reportBlend = bunker.bioBlend && Object.keys(bunker.bioBlend).length !== 0 ? bunker.bioBlend : null;

  if (reportBlend?.uuid) {
    const byUuid = bioBlends.find((blend) => blend.uuid === reportBlend.uuid);
    if (byUuid) {
      return byUuid;
    }
  }

  const blendName = reportBlend?.name?.toLowerCase() || bunker.grade?.toLowerCase();
  if (!blendName) {
    return undefined;
  }

  return bioBlends.find((blend) => blend.name.toLowerCase() === blendName);
};
